import { mapDTO } from '@twistezo/ts-dto-mapper'
import { omit } from 'lodash'

import { Schema } from 'features/inventory/domain/forms/RichPlacementsForm/sections/MultiAnchorForm/schema'
import { Ability } from 'providers/casl'
import { fieldsToOmit } from 'providers/tanstack-query/utils'
import { minutesToMilliseconds } from 'utils/number'
import { Variables } from './types'

type From = Schema
type To = Omit<Variables, 'domainId'>

export const toDTO = (from: From, ability: Ability): To =>
  mapDTO<From, To>({ from }).transform(
    ({
      adUnitIds,
      autoGenerateDivIdsEnabled,
      counterStyle,
      customizeAppearance,
      customizeBasicSettings,
      customStyling,
      customVariableName,
      customVariableTriggerEnabled,
      customVariableValue,
      desktopAdDisplaysBeforeRefresh,
      desktopSingleAdViewTime,
      displayAfterUserInteraction,
      enabled,
      frequencyCappingEnabled,
      fullWidthMode,
      impressionLimit,
      matchType,
      mobileAdDisplaysBeforeRefresh,
      mobileSingleAdViewTime,
      path,
      pathTriggerEnabled,
      showCloseButton,
      timeWindow,
    }: From): To => {
      const constantFields: Omit<To, 'adUnitIds' | 'autoGenerateDivIdsEnabled'> = {
        customizeApperanceEnabled: customizeAppearance,
        customizeBasicSettingsEnabled: customizeBasicSettings,
        customStyling: JSON.parse(customStyling),
        customVariableTriggerEnabled,
        ...(customVariableTriggerEnabled ? { customVariableName, customVariableValue } : {}),
        displayAfterUserInteractionEnabled: displayAfterUserInteraction,
        enabled,
        fullWidthModeEnabled: fullWidthMode,
        pathTriggerEnabled,
        ...(pathTriggerEnabled ? { matchType, path } : {}),
        counterStyle,
        desktopAdDisplaysBeforeRefresh: Number(desktopAdDisplaysBeforeRefresh),
        desktopSingleAdViewTime,
        mobileAdDisplaysBeforeRefresh: Number(mobileAdDisplaysBeforeRefresh),
        mobileSingleAdViewTime,
        showCloseButtonEnabled: showCloseButton,
      }

      const variableFields: Pick<
        To,
        | 'adUnitIds'
        | 'autoGenerateDivIdsEnabled'
        | 'frequencyCappingEnabled'
        | 'impressionLimit'
        | 'timeWindow'
      > = {
        adUnitIds: adUnitIds.right,
        autoGenerateDivIdsEnabled,
        frequencyCappingEnabled,
        impressionLimit,
        ...(timeWindow !== undefined ? { timeWindow: minutesToMilliseconds(timeWindow) } : {}),
      }

      return {
        ...constantFields,
        ...omit(
          variableFields,
          fieldsToOmit<Variables>({
            adUnitIds: ability.cannot(
              'update',
              'DomainRichPlacementsFeature-multiAnchorAdUnitConnection',
            ),
            autoGenerateDivIdsEnabled: ability.cannot(
              'update',
              'DomainRichPlacementsFeature-multiAnchorAdUnitConnection',
            ),
            frequencyCappingEnabled: ability.cannot('update', 'DomainField-frequencyCapping'),
            impressionLimit: ability.cannot('update', 'DomainField-frequencyCapping'),
            timeWindow: ability.cannot('update', 'DomainField-frequencyCapping'),
          }),
        ),
      }
    },
  )
